import { useCallback, useEffect, useRef, useState } from 'react'
import { Card, Table, Button, Space, Input, Select, DatePicker, Tag, App } from 'antd'
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import { importLogsApi } from '../../services/api'
import Permission from '../../components/Permission'

const { RangePicker } = DatePicker

type LogRow = {
  key: string
  importId: string
  importerName: string
  account: string
  importTime: string
  fileName: string
  module: string
  totalRows: number
  inserted: number
  updated: number
  failed: number
  ipAddress: string
  status: string
  hasFailureLog?: boolean
  errorMessage?: string
}

const STATUS_MAP: Record<string, { label: string; color: string }> = {
  pending: { label: '待处理', color: 'default' },
  processing: { label: '处理中', color: 'processing' },
  success: { label: '成功', color: 'green' },
  partial: { label: '部分成功', color: 'orange' },
  failure: { label: '失败', color: 'red' },
}

function canDownloadFailure(record: LogRow): boolean {
  if (record.status === 'pending' || record.status === 'processing') return false
  return Boolean(record.hasFailureLog) || record.failed > 0
}

function parseCsvLine(line: string): string[] {
  const fields: string[] = []
  let current = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        current += ch
      }
    } else if (ch === '"') {
      inQuotes = true
    } else if (ch === ',') {
      fields.push(current)
      current = ''
    } else {
      current += ch
    }
  }
  fields.push(current)
  return fields
}

function stringifyCsvRow(fields: string[]): string {
  return fields
    .map((field) => (/[",\r\n]/.test(field) ? `"${field.replace(/"/g, '""')}"` : field))
    .join(',')
}

/** 从失败数据 CSV 中移除「行号」列，保留「错误信息」供用户修正 */
function removeCsvColumn(csvText: string, columnName: string): string {
  const text = csvText.replace(/^\uFEFF/, '')
  const lines = text.split(/\r?\n/)
  if (!lines.length) return csvText

  const colIndex = parseCsvLine(lines[0]).findIndex((h) => h.trim() === columnName)
  if (colIndex === -1) return csvText

  return lines
    .filter((line, i) => line !== '' || i < lines.length - 1)
    .map((line) => {
      const fields = parseCsvLine(line)
      fields.splice(colIndex, 1)
      return stringifyCsvRow(fields)
    })
    .join('\r\n')
}

function triggerDownloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

async function downloadFailureCsv(blob: Blob, filename: string) {
  const text = await blob.text()
  const csv = removeCsvColumn(text, '行号')
  triggerDownloadBlob(new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' }), filename)
}

const ImportLogManagement = () => {
  const { message } = App.useApp()
  const [loading, setLoading] = useState(false)
  const [downloading, setDownloading] = useState<string | null>(null)
  const [data, setData] = useState<LogRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [keyword, setKeyword] = useState('')
  const [status, setStatus] = useState<string | undefined>()
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null)
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const buildFilters = useCallback(() => {
    const filters: Record<string, string> = {}
    if (keyword.trim()) filters.keyword = keyword.trim()
    if (status) filters.status = status
    if (dateRange?.[0] && dateRange?.[1]) {
      filters.dateFrom = dateRange[0].format('YYYY-MM-DD')
      filters.dateTo = dateRange[1].format('YYYY-MM-DD')
    }
    return Object.keys(filters).length ? filters : undefined
  }, [keyword, status, dateRange])

  const fetchList = useCallback(() => {
    setLoading(true)
    const filters = buildFilters()
    importLogsApi
      .list({ page, pageSize, filters })
      .then((res: unknown) => {
        const raw = res as { data?: { items?: LogRow[]; total?: number } }
        const items = raw?.data?.items ?? []
        setData(
          items.map((it, i) => ({
            ...it,
            key: String(it.importId ?? i),
            totalRows: Number(it.totalRows ?? 0),
            inserted: Number(it.inserted ?? 0),
            updated: Number(it.updated ?? 0),
            failed: Number(it.failed ?? 0),
          })),
        )
        setTotal(Number(raw?.data?.total ?? items.length))
      })
      .catch(() => {
        setData([])
        setTotal(0)
        message.error('加载导入日志失败')
      })
      .finally(() => setLoading(false))
  }, [page, pageSize, buildFilters, message])

  useEffect(() => {
    fetchList()
  }, [fetchList])

  useEffect(() => {
    const hasPending = data.some((r) => r.status === 'pending' || r.status === 'processing')
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current)
      pollTimerRef.current = null
    }
    if (hasPending) {
      pollTimerRef.current = setInterval(() => fetchList(), 3000)
    }
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current)
    }
  }, [data, fetchList])

  const handleDownload = async (importId: string) => {
    const key = `${importId}-failure`
    setDownloading(key)
    try {
      const res = await importLogsApi.download({ importId, type: 'failure' })
      if (!(res instanceof Blob)) return
      const filename = `导入失败数据_${importId.slice(0, 8)}.csv`
      if (res.type.includes('json')) {
        const text = await res.text()
        try {
          const json = JSON.parse(text) as { success?: boolean; message?: string }
          if (json.success === false) {
            message.error(json.message || '下载失败')
            return
          }
        } catch {
          await downloadFailureCsv(new Blob([text], { type: 'text/csv;charset=utf-8' }), filename)
          message.success('下载成功')
          return
        }
      }
      await downloadFailureCsv(res, filename)
      message.success('下载成功')
    } catch {
      message.error('下载失败')
    } finally {
      setDownloading(null)
    }
  }

  const columns = [
    { title: '导入人', dataIndex: 'importerName', key: 'importerName', width: 120 },
    { title: '账号', dataIndex: 'account', key: 'account', width: 110 },
    { title: '导入时间', dataIndex: 'importTime', key: 'importTime', width: 170 },
    { title: '文件名', dataIndex: 'fileName', key: 'fileName', width: 180, ellipsis: true },
    { title: '模块', dataIndex: 'module', key: 'module', width: 90 },
    { title: '新增', dataIndex: 'inserted', key: 'inserted', width: 70 },
    { title: '更新', dataIndex: 'updated', key: 'updated', width: 70 },
    { title: '失败', dataIndex: 'failed', key: 'failed', width: 70 },
    { title: 'IP地址', dataIndex: 'ipAddress', key: 'ipAddress', width: 130 },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s: string) => {
        const m = STATUS_MAP[s] ?? { label: s, color: 'default' }
        return <Tag color={m.color}>{m.label}</Tag>
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right' as const,
      render: (_: unknown, record: LogRow) =>
        canDownloadFailure(record) ? (
          <Permission codes={['import_logs:download', 'data:import', 'data_sources:import']}>
            <Button
              type="link"
              size="small"
              danger
              icon={<DownloadOutlined />}
              loading={downloading === `${record.importId}-failure`}
              onClick={() => void handleDownload(record.importId)}
            >
              下载失败数据
            </Button>
          </Permission>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold mb-2">导入日志</h1>
        <p className="text-gray-600">
          查看资产数据异步导入任务记录。导入成功无需下载；若有失败记录，可下载失败数据（与导入模板列一致，并附带「错误信息」列），修正后重新上传导入（「错误信息」列重新导入时会自动忽略）
        </p>
      </div>

      <Card title="筛选条件">
        <Space wrap className="w-full">
          <Input
            placeholder="导入人 / 账号 / 文件名"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            style={{ width: 220 }}
            allowClear
          />
          <Select
            placeholder="任务状态"
            allowClear
            style={{ width: 140 }}
            value={status}
            onChange={setStatus}
            options={[
              { value: 'pending', label: '待处理' },
              { value: 'processing', label: '处理中' },
              { value: 'success', label: '成功' },
              { value: 'partial', label: '部分成功' },
              { value: 'failure', label: '失败' },
            ]}
          />
          <RangePicker value={dateRange} onChange={(d) => setDateRange(d as [Dayjs | null, Dayjs | null] | null)} />
          <Button type="primary" onClick={() => { setPage(1); fetchList() }}>
            查询
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              setKeyword('')
              setStatus(undefined)
              setDateRange(null)
              setPage(1)
              fetchList()
            }}
          >
            重置
          </Button>
        </Space>
      </Card>

      <Card title="日志列表">
        <Table
          rowKey="key"
          loading={loading}
          columns={columns}
          dataSource={data}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (p, ps) => {
              setPage(p)
              setPageSize(ps)
            },
          }}
          scroll={{ x: 1300 }}
        />
      </Card>
    </div>
  )
}

export default ImportLogManagement
